import 'chartjs-adapter-moment';

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AxsIconComponent } from '@vanguard/axs-angular';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { SharedInfoService } from '../../shared/services/shared-info/shared-info.service';
import { AssetLineData, LineData } from '../../shared/util/calculation.interface';
import { DiscoverModalComponent } from '../discover-modal/discover-modal.component';
import type { ThesisType } from '../thesis/thesis.component';

Chart.register(...registerables);

const COLOR_TEAL_DOT = '#1BA39C';
const COLOR_YELLOW_DOT = '#FFD780';
const COLOR_WITHOUT_CONV_BORDER = '#B87D00';
const COLOR_WITH_CONV_BORDER = '#003C3A';
const COLOR_DESIRABLE_HEADER_BG = '#007873';
const COLOR_UNDESIRABLE_HEADER_BG = '#3A3A3A';
const COLOR_WITH_CONV_TEXT = '#005A56';
const COLOR_AXIS_LABEL_GRAY = '#4A4A4A';
const COLOR_GRID = '#E5E5E5';
const COLOR_BETR_BAR_BG = '#FFF7E6';
const COLOR_EQUAL_HEADER_BG = '#EAEAEA';
const COLOR_FUTURE_TAX_BAR_BG = '#691418';
const FONT_FAMILY = 'FF Mark, Helvetica, Arial, sans-serif';
const MOBILE_BREAKPOINT = 650;
const DESKTOP_TOOLTIP_WIDTH = 238;
const TOOLTIP_HEADER_H = 33;
const TOOLTIP_MIDDLE_H = 60;
const TOOLTIP_BETR_H = 21;
const TOOLTIP_FUTURE_TAX_H = 17;
const DESKTOP_TOOLTIP_HEIGHT =
  TOOLTIP_HEADER_H + TOOLTIP_MIDDLE_H + TOOLTIP_BETR_H + TOOLTIP_FUTURE_TAX_H;
const CHART_ASPECT_RATIO = 157 / 82;
const TRIANGLE_SIDE = 18;
const TRIANGLE_HEIGHT = (TRIANGLE_SIDE * Math.sqrt(3)) / 2;
const TRIANGLE_CENTROID_OFFSET = TRIANGLE_HEIGHT / 3;
const DASHED_LINE_HEIGHT = 10;
const DASHED_LINE_GAP = 2;
const BREAK_EVEN_LABEL_FONT_SIZE = 14;
const BULB_W = 16;
const BULB_H = 23;

interface ChartSeriesContext {
  years: number[];
  withConversion: number[];
  withoutConversion: number[];
  hasBreakEven: boolean;
  breakEvenX: number;
  breakEvenY: number;
  defaultTooltipX: number;
}

interface AfterDrawContext {
  chart: Chart;
  ctx: CanvasRenderingContext2D;
  years: number[];
  withConversion: number[];
  withoutConversion: number[];
  hasBreakEven: boolean;
  breakEvenX: number;
  breakEvenY: number;
  defaultTooltipX: number;
  defaultTooltipIdx: number;
  bulbPixelX: number;
  bulbPixelY: number;
  bulbWithConv: number;
  bulbWithoutConv: number;
  activeTooltipX: number;
  activeTooltipIdx: number;
  activeTooltipWithConv: number;
  activeTooltipWithoutConv: number;
  tooltipPixelX: number;
  tooltipPixelY: number;
  withoutConvPixelY: number;
  boxX: number;
  boxY: number;
}

@Component({
  selector: 'graph-section-v4',
  standalone: true,
  imports: [CommonModule, AxsIconComponent, DiscoverModalComponent],
  templateUrl: './graph-section-v4.component.html',
  styleUrl: './graph-section-v4.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GraphSectionV4Component implements OnInit, AfterViewInit, OnDestroy {
  @Input() currentThesisType: ThesisType = 'profitable';

  @ViewChild('chartCanvas') private chartCanvas: ElementRef<HTMLCanvasElement> | undefined;
  private chartInstance: Chart | null = null;
  private chartContent = content.Data.v4_rcc_root.content.chart;
  private desirableProfitabilityPointContent =
    content.Data.v4_rcc_root.content.desirableProfitabilityPoint;
  private iraNoteContent = content.Data.v4_rcc_root.content.iraNote;
  private assetGrowthData: AssetLineData | null = null;
  private breakEvenData: LineData[] | null = null;
  private formFutureTaxRate: number = 0;
  // The future rate BXR ran this chart with; null on the toggle-off local path.
  private bxrFutureTaxRate: number | null = null;

  // Shown in the flag next to the break-even rate, so it must be the rate the chart used.
  private get futureTaxRate(): number {
    return this.bxrFutureTaxRate ?? this.formFutureTaxRate;
  }
  private graphDataSub: any;
  private futureTaxRateSub: any;
  private yearsUntilDistSub: any;
  private howWillYouPaySub: any;
  private inputsValidSub: any;
  private inputsCompleteSub: any;
  private resizeListener: (() => void) | null = null;
  private yearsUntilDist: number | null = null;
  private hoveredYear: number | null = null;
  private defaultTooltipYear: number | null = null;
  private mouseMoveListener: ((e: MouseEvent) => void) | null = null;
  private mouseLeaveListener: (() => void) | null = null;
  private howWillYouPay: string | null = null;
  isDiscoverModalOpen = false;
  isMobile: boolean = window.innerWidth < MOBILE_BREAKPOINT;
  mobileTooltip: {
    headerText: string;
    headerColor: string;
    headerTextColor: string;
    withConvValue: string;
    withoutConvValue: string;
    withConvLabel: string;
    withoutConvLabel: string;
    valuesEqual: boolean;
    desirable: boolean;
    withConvOnTop: boolean;
    betrLabel: string;
    betrValue: string;
    futureTaxLabel: string;
    futureTaxValue: string;
  } | null = null;
  private bulbImageShouldConvert: HTMLImageElement = this.preloadImage(
    'assets/images/Bulb-Chart-Convert.svg',
  );
  private bulbImageDontConvert: HTMLImageElement = this.preloadImage(
    'assets/images/Bulb-Chart-DontConvert.svg',
  );
  private bulbImageIndifferent: HTMLImageElement = this.preloadImage(
    'assets/images/Bulb-Chart-Indifferent.svg',
  );
  private betrIcon: HTMLImageElement = this.preloadImage('assets/images/BETR-Icon-Chart.svg');
  private lineImage: HTMLImageElement = this.preloadImage('assets/svgs/Line.svg');

  constructor(
    private readonly contentService: ContentService,
    private sharedInfoService: SharedInfoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.generateChart();
  }

  ngOnInit(): void {
    this.contentService.getContent().subscribe((data) => {
      this.chartContent = data.Data.v4_rcc_root.content.chart;
      this.desirableProfitabilityPointContent =
        data.Data.v4_rcc_root.content.desirableProfitabilityPoint;
      this.iraNoteContent = data.Data.v4_rcc_root.content.iraNote;
      this.generateChart();
      this.cdr.detectChanges();
    });
    this.howWillYouPaySub = this.sharedInfoService.getHowWillYouPay$().subscribe((value) => {
      this.howWillYouPay = value;
      this.cdr.detectChanges();
    });
    this.graphDataSub = this.sharedInfoService.getGraphData$().subscribe((graphData) => {
      if (graphData && graphData.assetGrowthData) {
        this.assetGrowthData = graphData.assetGrowthData;
        this.breakEvenData = graphData.breakEvenData;
        this.bxrFutureTaxRate =
          graphData.taxImpact?.taxRateDerivationDetails.futureIncomeTaxRate ?? null;
        this.generateChart();
      }
    });
    this.futureTaxRateSub = this.sharedInfoService.getFutureTaxRate$().subscribe((rate) => {
      this.formFutureTaxRate = rate;
      this.generateChart();
    });
    this.yearsUntilDistSub = this.sharedInfoService.getYearsUntilDist$().subscribe((years) => {
      this.yearsUntilDist = years;
      this.generateChart();
    });
    this.inputsValidSub = this.sharedInfoService.getInputsValid$().subscribe(() => {
      this.generateChart();
    });
    this.inputsCompleteSub = this.sharedInfoService.getInputsComplete$().subscribe(() => {
      this.generateChart();
      this.cdr.detectChanges();
    });
    this.resizeListener = () => {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      this.cdr.detectChanges();
      this.generateChart();
    };
    window.addEventListener('resize', this.resizeListener);
  }

  get hasProfitabilityPoint(): boolean {
    if (!this.chartHasResult || !this.assetGrowthData) {
      return false;
    }

    const nonConversionByYear = new Map<number, number>();
    for (const point of this.assetGrowthData.nonConversionLineData) {
      nonConversionByYear.set(point.year, point.value);
    }

    for (const point of this.assetGrowthData.conversionLineData) {
      const nonConvValue = nonConversionByYear.get(point.year);
      if (nonConvValue !== undefined && point.value > nonConvValue) {
        return true;
      }
    }

    return false;
  }

  get profitabilityLegendTitle(): string {
    return this.desirableProfitabilityPointContent?.profitabilitypoint || '';
  }

  get profitabilityLegendDescription(): string {
    return this.desirableProfitabilityPointContent?.desirableprofitabilitydesc || '';
  }

  // Errors and blank fields from Clear all fields both leave nothing to plot.
  private get chartHasResult(): boolean {
    return this.sharedInfoService.inputsValid && this.sharedInfoService.inputsComplete;
  }

  get showIraNote(): boolean {
    if (!this.chartHasResult) {
      return false;
    }

    const method = (this.howWillYouPay || '').toLowerCase();

    const isUndesirableOrIndifferentState =
      this.currentThesisType === 'loss' || this.currentThesisType === 'no-difference';

    return method.includes('ira') && isUndesirableOrIndifferentState;
  }

  get iraNoteTitle(): string {
    return this.iraNoteContent?.pleasenote || '';
  }

  get iraNoteBody(): string {
    return this.iraNoteContent?.iranote || '';
  }

  get iraNoteDiscoverText(): string {
    return this.iraNoteContent?.iranotediscover || '';
  }

  get iraNoteDesc(): string {
    return this.iraNoteContent?.iranotedesc || '';
  }

  openDiscoverModal(): void {
    this.isDiscoverModalOpen = true;
  }

  // eslint-disable-next-line max-statements
  private generateEmptyChart() {
    const canvas = this.chartCanvas?.nativeElement;
    if (!canvas) {
      return;
    }

    this.removeMouseListeners(canvas);
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }

    const container = canvas.parentElement;
    if (container) {
      const containerWidth = container.clientWidth;
      canvas.width = containerWidth;
      canvas.height = Math.round(containerWidth / CHART_ASPECT_RATIO);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx || !this.chartContent) {
      return;
    }

    // Create an empty chart with no data - just axes and grid
    const config: ChartConfiguration = {
      type: 'line',
      data: {
        labels: [],
        datasets: [],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          title: { display: false },
        },
        layout: { padding: 0 },
        scales: {
          x: {
            type: 'linear',
            title: {
              display: true,
              text: this.chartContent.xAxisLabel,
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 13 },
            },
            min: 0,
            max: 50,
            ticks: {
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 12 },
              stepSize: 10,
            },
            grid: {
              color: COLOR_GRID,
              lineWidth: 1,
            },
          },
          y: {
            title: {
              display: true,
              text: this.chartContent.yAxisLabel,
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 13 },
            },
            min: 0,
            ticks: {
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 12 },
            },
            grid: {
              color: COLOR_GRID,
              lineWidth: 1,
            },
          },
        },
        animation: false,
      },
    };

    this.chartInstance = new Chart(ctx, config);
  }

  // eslint-disable-next-line max-statements
  private generateChart() {
    if (!this.chartHasResult) {
      this.generateEmptyChart();
      return;
    }

    const canvas = this.chartCanvas?.nativeElement;
    if (!this.assetGrowthData || !canvas || !this.chartContent) {
      return;
    }

    this.removeMouseListeners(canvas);
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }

    const container = canvas.parentElement;
    if (container) {
      const containerWidth = container.clientWidth;
      canvas.width = containerWidth;
      canvas.height = Math.round(containerWidth / CHART_ASPECT_RATIO);
    }

    const years = this.assetGrowthData.conversionLineData.map((d) => d.year);
    const withConversion = this.assetGrowthData.conversionLineData.map((d) => d.value);
    const withoutConversion = this.assetGrowthData.nonConversionLineData.map((d) => d.value);

    // Find break-even year (first year when withConversion > withoutConversion)
    let breakEvenIdx = withConversion.findIndex((v, i) => v > withoutConversion[i]);
    const hasBreakEven = breakEvenIdx >= 0;
    if (breakEvenIdx < 0) breakEvenIdx = 0;
    const breakEvenX = years[breakEvenIdx];
    const breakEvenY = withConversion[breakEvenIdx];
    // Find default tooltip position snapped to nearest year in data for yearsUntilDist
    let defaultTooltipX = breakEvenX;
    if (this.yearsUntilDist != null && !isNaN(this.yearsUntilDist)) {
      const idx = years.indexOf(this.yearsUntilDist);
      if (idx !== -1) {
        defaultTooltipX = years[idx];
      } else {
        defaultTooltipX = this.findClosestYear(years, this.yearsUntilDist);
      }
    }
    this.defaultTooltipYear = defaultTooltipX;
    const mobileTooltipYear =
      this.hoveredYear !== null && years.includes(this.hoveredYear)
        ? this.hoveredYear
        : defaultTooltipX;
    this.buildMobileTooltip(mobileTooltipYear, years, withConversion, withoutConversion);
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const config: ChartConfiguration = {
      type: 'line',
      data: {
        labels: years,
        datasets: [
          {
            label: this.chartContent.withoutConversion,
            data: withoutConversion,
            borderColor: COLOR_WITHOUT_CONV_BORDER,
            backgroundColor: 'rgba(255, 215, 128, 0.5)',
            borderWidth: 1.2,
            pointRadius: 0,
            fill: 'origin',
            tension: 0.18,
            order: 1,
          },
          {
            label: this.chartContent.withConversion,
            data: withConversion,
            borderColor: COLOR_WITH_CONV_BORDER,
            backgroundColor: 'rgba(27, 163, 156, 0.43)',
            borderWidth: 1.2,
            pointRadius: 0,
            fill: 'origin',
            tension: 0.18,
            order: 2,
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          title: { display: false },
        },
        layout: { padding: 0 },
        scales: {
          x: {
            type: 'linear',
            title: {
              display: true,
              text: this.chartContent.xAxisLabel,
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 13 },
            },
            min: Math.min(...years),
            max: Math.max(...years),
            ticks: {
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 12 },
              stepSize: 10,
              callback: function (val) {
                return val;
              },
            },
            grid: {
              color: COLOR_GRID,
              lineWidth: 1,
            },
          },
          y: {
            title: {
              display: true,
              text: this.chartContent.yAxisLabel,
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 13 },
            },
            min: 0,
            suggestedMax: Math.max(...withConversion, ...withoutConversion),
            ticks: {
              color: COLOR_AXIS_LABEL_GRAY,
              font: { family: FONT_FAMILY, size: 12 },
              callback: function (val) {
                const num = typeof val === 'string' ? parseFloat(val) : val;
                if (num >= 1000000000000) {
                  return (num / 1000000000000).toFixed(num % 1000000000000 === 0 ? 0 : 1) + 'T';
                }
                if (num >= 1000000000) {
                  return (num / 1000000000).toFixed(num % 1000000000 === 0 ? 0 : 1) + 'B';
                }
                if (num >= 1000000) {
                  return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1) + 'M';
                }
                if (num >= 1000) {
                  return num / 1000 + 'k';
                }
                return num;
              },
            },
            grid: {
              color: COLOR_GRID,
              lineWidth: 1,
            },
          },
        },
        animation: false,
      },
      plugins: [
        {
          id: 'custom-annotation',
          afterDraw: (chart) => {
            this.runAfterDraw(chart, {
              years,
              withConversion,
              withoutConversion,
              hasBreakEven,
              breakEvenX,
              breakEvenY,
              defaultTooltipX,
            });
          },
        },
      ],
    };
    this.chartInstance = new Chart(ctx, config);
    if (canvas && this.chartInstance) {
      this.setupMouseListeners(canvas, this.chartInstance);
    }
  }

  // eslint-disable-next-line max-statements
  private runAfterDraw(chart: Chart, series: ChartSeriesContext): void {
    const {
      years,
      withConversion,
      withoutConversion,
      hasBreakEven,
      breakEvenX,
      breakEvenY,
      defaultTooltipX,
    } = series;
    const ctx = chart.ctx;
    const defaultTooltipIdx = years.indexOf(defaultTooltipX);
    const bulbWithConv =
      defaultTooltipIdx !== -1 ? withConversion[defaultTooltipIdx] : withConversion[0];
    const bulbWithoutConv =
      defaultTooltipIdx !== -1 ? withoutConversion[defaultTooltipIdx] : withoutConversion[0];
    const bulbPixelX = chart.scales.x.getPixelForValue(defaultTooltipX);
    const bulbPixelY = chart.scales.y.getPixelForValue(bulbWithConv);

    const activeTooltipX = this.hoveredYear !== null ? this.hoveredYear : defaultTooltipX;
    const activeTooltipIdx = years.indexOf(activeTooltipX);
    const activeTooltipWithConv =
      activeTooltipIdx !== -1
        ? withConversion[activeTooltipIdx]
        : withConversion[defaultTooltipIdx];
    const activeTooltipWithoutConv =
      activeTooltipIdx !== -1
        ? withoutConversion[activeTooltipIdx]
        : withoutConversion[defaultTooltipIdx];
    const tooltipPixelX = chart.scales.x.getPixelForValue(activeTooltipX);
    const tooltipPixelY = chart.scales.y.getPixelForValue(activeTooltipWithConv);
    const withoutConvPixelY = chart.scales.y.getPixelForValue(activeTooltipWithoutConv);

    const boxW = DESKTOP_TOOLTIP_WIDTH;
    const boxH = DESKTOP_TOOLTIP_HEIGHT;
    const edgeMargin = 8;
    const preferredBoxX = tooltipPixelX;
    const preferredBoxY = tooltipPixelY - boxH - 48;
    const boxX = Math.max(
      chart.scales.x.left + boxW / 2 + edgeMargin,
      Math.min(chart.scales.x.right - boxW / 2 - edgeMargin, preferredBoxX),
    );
    const flippedBelow = preferredBoxY < chart.scales.y.top;
    const preferredBoxYAfterFlip = flippedBelow ? tooltipPixelY + 48 : preferredBoxY;
    const boxY = Math.max(
      chart.scales.y.top,
      Math.min(chart.scales.y.bottom - boxH, preferredBoxYAfterFlip),
    );

    const drawCtx: AfterDrawContext = {
      chart,
      ctx,
      years,
      withConversion,
      withoutConversion,
      hasBreakEven,
      breakEvenX,
      breakEvenY,
      defaultTooltipX,
      defaultTooltipIdx,
      bulbPixelX,
      bulbPixelY,
      bulbWithConv,
      bulbWithoutConv,
      activeTooltipX,
      activeTooltipIdx,
      activeTooltipWithConv,
      activeTooltipWithoutConv,
      tooltipPixelX,
      tooltipPixelY,
      withoutConvPixelY,
      boxX,
      boxY,
    };

    if (hasBreakEven) {
      this.drawTriangleMarker(drawCtx);
    }
    this.drawCrosshair(drawCtx);
    if (this.hoveredYear !== null) {
      this.drawHoverDots(drawCtx);
    }
    this.drawBulbIcon(drawCtx);
    if (!this.isMobile) {
      this.drawDesktopTooltip(drawCtx);
    }
  }

  // eslint-disable-next-line max-statements
  private drawTriangleMarker(c: AfterDrawContext): void {
    const { ctx, chart, breakEvenX, breakEvenY, bulbPixelX, defaultTooltipX } = c;
    const breakEvenPixelX = chart.scales.x.getPixelForValue(breakEvenX);
    const breakEvenPixelY = chart.scales.y.getPixelForValue(breakEvenY);

    ctx.beginPath();
    ctx.moveTo(breakEvenPixelX, breakEvenPixelY - TRIANGLE_HEIGHT + TRIANGLE_CENTROID_OFFSET);
    ctx.lineTo(breakEvenPixelX - TRIANGLE_SIDE / 2, breakEvenPixelY + TRIANGLE_CENTROID_OFFSET);
    ctx.lineTo(breakEvenPixelX + TRIANGLE_SIDE / 2, breakEvenPixelY + TRIANGLE_CENTROID_OFFSET);
    ctx.closePath();
    ctx.fillStyle = '#E8F6F5';
    ctx.fill();
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Label — side chosen to avoid horizontal overlap with bulb icon
    ctx.font = `bold ${BREAK_EVEN_LABEL_FONT_SIZE}px ${FONT_FAMILY}`;
    ctx.fillStyle = COLOR_WITH_CONV_TEXT;
    const labelText = `${breakEvenX} years`;
    const labelW = ctx.measureText(labelText).width;
    const pointsIdenticalForLabel = breakEvenX === defaultTooltipX;
    const triangleTopY = breakEvenPixelY - TRIANGLE_HEIGHT + TRIANGLE_CENTROID_OFFSET;
    const lineTopY = triangleTopY - DASHED_LINE_HEIGHT - DASHED_LINE_GAP;
    const lineCenterY = lineTopY + DASHED_LINE_HEIGHT / 2;
    const labelY = pointsIdenticalForLabel ? lineCenterY : triangleTopY - 4;
    const labelGap = 4;
    const chartLeft = chart.scales.x.left;
    const chartRight = chart.scales.x.right;
    const rightLabelRight = breakEvenPixelX + labelGap + labelW;
    const leftLabelLeft = breakEvenPixelX - labelGap - labelW;
    const bulbLeft = bulbPixelX - BULB_W / 2;
    const bulbRight = bulbPixelX + BULB_W / 2;
    const rightOverlapsBulb = rightLabelRight > bulbLeft && breakEvenPixelX + labelGap < bulbRight;
    const leftLabelOutOfBounds = leftLabelLeft < chartLeft;
    const rightLabelOutOfBounds = rightLabelRight > chartRight;
    const placeOnRight = leftLabelOutOfBounds || (!rightOverlapsBulb && !rightLabelOutOfBounds);

    if (placeOnRight) {
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, breakEvenPixelX + labelGap, labelY);
    } else {
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, breakEvenPixelX - labelGap, labelY);
    }
    ctx.restore();
  }

  private drawCrosshair(c: AfterDrawContext): void {
    const { ctx, chart, tooltipPixelX } = c;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(tooltipPixelX, chart.scales.y.bottom);
    ctx.lineTo(tooltipPixelX, chart.scales.y.top);
    ctx.strokeStyle = COLOR_AXIS_LABEL_GRAY;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.stroke();
    ctx.restore();
  }

  // eslint-disable-next-line max-statements
  private drawHoverDots(c: AfterDrawContext): void {
    const {
      ctx,
      tooltipPixelX,
      tooltipPixelY,
      withoutConvPixelY,
      activeTooltipWithConv,
      activeTooltipWithoutConv,
    } = c;
    const dotRadius = 6;
    const dotsValuesEqual =
      this.truncate(activeTooltipWithConv) === this.truncate(activeTooltipWithoutConv);

    if (dotsValuesEqual) {
      // Half-teal (left) / half-yellow (right) dot for indifferent scenario
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tooltipPixelX, tooltipPixelY);
      ctx.arc(tooltipPixelX, tooltipPixelY, dotRadius, Math.PI / 2, (Math.PI * 3) / 2);
      ctx.closePath();
      ctx.fillStyle = COLOR_TEAL_DOT;
      ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tooltipPixelX, tooltipPixelY);
      ctx.arc(tooltipPixelX, tooltipPixelY, dotRadius, -Math.PI / 2, Math.PI / 2);
      ctx.closePath();
      ctx.fillStyle = COLOR_YELLOW_DOT;
      ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.arc(tooltipPixelX, tooltipPixelY, dotRadius, 0, Math.PI * 2);
      ctx.strokeStyle = '#222';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    } else {
      const desirable = activeTooltipWithConv > activeTooltipWithoutConv;
      const firstDotY = desirable ? withoutConvPixelY : tooltipPixelY;
      const firstDotColor = desirable ? COLOR_YELLOW_DOT : COLOR_TEAL_DOT;
      const secondDotY = desirable ? tooltipPixelY : withoutConvPixelY;
      const secondDotColor = desirable ? COLOR_TEAL_DOT : COLOR_YELLOW_DOT;
      ctx.save();
      ctx.beginPath();
      ctx.arc(tooltipPixelX, firstDotY, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = firstDotColor;
      ctx.fill();
      ctx.strokeStyle = '#222';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.arc(tooltipPixelX, secondDotY, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = secondDotColor;
      ctx.fill();
      ctx.strokeStyle = '#222';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawBulbIcon(c: AfterDrawContext): void {
    const {
      ctx,
      chart,
      hasBreakEven,
      breakEvenX,
      defaultTooltipX,
      bulbPixelX,
      bulbPixelY,
      bulbWithConv,
      bulbWithoutConv,
    } = c;

    const activeBulb =
      this.truncate(bulbWithConv) === this.truncate(bulbWithoutConv)
        ? this.bulbImageIndifferent
        : bulbWithConv > bulbWithoutConv
          ? this.bulbImageShouldConvert
          : this.bulbImageDontConvert;

    const pointsAreIdentical = hasBreakEven && breakEvenX === defaultTooltipX;

    if (pointsAreIdentical) {
      const triangleTopY = bulbPixelY - TRIANGLE_HEIGHT + TRIANGLE_CENTROID_OFFSET;
      let lineTopY = triangleTopY - DASHED_LINE_HEIGHT - DASHED_LINE_GAP;
      let bulbTopY = lineTopY - BULB_H;
      const chartTop = chart.scales.y.top;
      if (bulbTopY < chartTop) {
        bulbTopY = chartTop;
        lineTopY = bulbTopY + BULB_H;
      }
      ctx.drawImage(this.lineImage, bulbPixelX - 0.5, lineTopY, 1, DASHED_LINE_HEIGHT);
      ctx.drawImage(activeBulb, bulbPixelX - BULB_W / 2, bulbTopY, BULB_W, BULB_H);
    } else {
      ctx.drawImage(activeBulb, bulbPixelX - BULB_W / 2, bulbPixelY - BULB_H / 2, BULB_W, BULB_H);
    }
  }

  // eslint-disable-next-line max-statements
  private drawDesktopTooltip(c: AfterDrawContext): void {
    const { ctx, activeTooltipX, activeTooltipWithConv, activeTooltipWithoutConv, boxX, boxY } = c;
    const boxW = DESKTOP_TOOLTIP_WIDTH;
    const boxH = DESKTOP_TOOLTIP_HEIGHT;
    const activeTooltipDifference = activeTooltipWithConv - activeTooltipWithoutConv;
    const desirableTooltipScenario = activeTooltipWithConv > activeTooltipWithoutConv;
    const valuesEqual =
      this.truncate(activeTooltipWithConv) === this.truncate(activeTooltipWithoutConv);
    // Row order flips only when withConv is STRICTLY less — equal keeps withConv on top
    const withConvOnTop =
      this.truncate(activeTooltipWithConv) >= this.truncate(activeTooltipWithoutConv);

    // Box shadow and white fill — border drawn last so it sits above inner fills
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(boxX - boxW / 2, boxY, boxW, boxH, 10);
    ctx.shadowColor = 'rgba(0,0,0,0.10)';
    ctx.shadowBlur = 10;
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.restore();

    // Derive section Y positions cumulatively
    const middleY = boxY + TOOLTIP_HEADER_H;
    const dividerY = middleY + TOOLTIP_MIDDLE_H;
    const futureBarY = dividerY + TOOLTIP_BETR_H;

    // Header bar (rounded top corners only)
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(boxX - boxW / 2, boxY, boxW, TOOLTIP_HEADER_H, [10, 10, 0, 0]);
    ctx.fillStyle = valuesEqual
      ? COLOR_EQUAL_HEADER_BG
      : desirableTooltipScenario
        ? COLOR_DESIRABLE_HEADER_BG
        : COLOR_UNDESIRABLE_HEADER_BG;
    ctx.fill();
    ctx.font = `bold 14px ${FONT_FAMILY}`;
    ctx.fillStyle = valuesEqual ? '#232323' : '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const savedByText = desirableTooltipScenario
      ? this.chartContent.savedByConversion
      : this.chartContent.lostByConversion;
    ctx.fillText(
      this.formatCurrency(activeTooltipDifference) + ' ' + savedByText,
      boxX,
      boxY + TOOLTIP_HEADER_H / 2,
    );
    ctx.restore();

    // Middle section — two rows with colored dots and value/label pairs
    ctx.save();
    ctx.beginPath();
    ctx.rect(boxX - boxW / 2 + 1, middleY, boxW - 2, TOOLTIP_MIDDLE_H);
    ctx.fillStyle = '#fff';
    ctx.fill();
    const middleSectionCenterY = middleY + TOOLTIP_MIDDLE_H / 2;
    const topRowY = middleSectionCenterY - 9;
    const bottomRowY = middleSectionCenterY + 9;
    const topValue = withConvOnTop ? activeTooltipWithConv : activeTooltipWithoutConv;
    const topLabel =
      ' ' +
      (withConvOnTop ? this.chartContent.withConversion : this.chartContent.withoutConversion);
    const bottomValue = withConvOnTop ? activeTooltipWithoutConv : activeTooltipWithConv;
    const bottomLabel =
      ' ' +
      (withConvOnTop ? this.chartContent.withoutConversion : this.chartContent.withConversion);
    const legendDotRadius = 5;
    const legendDotX = boxX - boxW / 2 + 14;
    const legendTextX = legendDotX + legendDotRadius + 7;
    const textRightBound = boxX + boxW / 2 - 8;
    const topTextColor = valuesEqual
      ? '#232323'
      : withConvOnTop
        ? COLOR_WITH_CONV_TEXT
        : COLOR_AXIS_LABEL_GRAY;
    const bottomTextColor = valuesEqual
      ? '#232323'
      : withConvOnTop
        ? COLOR_AXIS_LABEL_GRAY
        : COLOR_WITH_CONV_TEXT;
    ctx.save();
    ctx.beginPath();
    ctx.arc(legendDotX, topRowY, legendDotRadius, 0, Math.PI * 2);
    ctx.fillStyle = withConvOnTop ? COLOR_TEAL_DOT : COLOR_YELLOW_DOT;
    ctx.fill();
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
    ctx.font = `bold 16px ${FONT_FAMILY}`;
    const topValueWidth = ctx.measureText(this.formatCurrency(topValue)).width;
    ctx.fillStyle = topTextColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.formatCurrency(topValue), legendTextX, topRowY);
    ctx.font = `11px ${FONT_FAMILY}`;
    ctx.fillStyle = topTextColor;
    ctx.fillText(
      this.ellipsize(ctx, topLabel, textRightBound - legendTextX - topValueWidth),
      legendTextX + topValueWidth,
      topRowY,
    );
    ctx.save();
    ctx.beginPath();
    ctx.arc(legendDotX, bottomRowY, legendDotRadius, 0, Math.PI * 2);
    ctx.fillStyle = withConvOnTop ? COLOR_YELLOW_DOT : COLOR_TEAL_DOT;
    ctx.fill();
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
    ctx.font = `bold 14px ${FONT_FAMILY}`;
    const bottomValueWidth = ctx.measureText(this.formatCurrency(bottomValue)).width;
    ctx.fillStyle = bottomTextColor;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.formatCurrency(bottomValue), legendTextX, bottomRowY);
    ctx.font = `11px ${FONT_FAMILY}`;
    ctx.fillStyle = bottomTextColor;
    ctx.fillText(
      this.ellipsize(ctx, bottomLabel, textRightBound - legendTextX - bottomValueWidth),
      legendTextX + bottomValueWidth,
      bottomRowY,
    );
    ctx.restore();

    // Divider between middle section and BETR bar
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(boxX - boxW / 2, dividerY);
    ctx.lineTo(boxX + boxW / 2, dividerY);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // BETR bar
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(boxX - boxW / 2 + 1, dividerY, boxW - 2, TOOLTIP_BETR_H, 0);
    ctx.fillStyle = COLOR_BETR_BAR_BG;
    ctx.fill();
    const betrIconSize = 12;
    const betrIconGap = 4;
    const betrBarCenterY = dividerY + TOOLTIP_BETR_H / 2;
    const breakEvenLabel = this.chartContent.breakEvenTaxRate + ' ';
    const betrAtTooltip = this.breakEvenData?.find((d) => d.year === activeTooltipX)?.value ?? 0;
    const breakEvenValue = Math.round((betrAtTooltip + Number.EPSILON) * 100) + '%';
    ctx.font = `12px ${FONT_FAMILY}`;
    const beLabelWidth = ctx.measureText(breakEvenLabel).width;
    ctx.font = `bold 12px ${FONT_FAMILY}`;
    const beValueWidth = ctx.measureText(breakEvenValue).width;
    const beGroupStartX = boxX - (betrIconSize + betrIconGap + beLabelWidth + beValueWidth) / 2;
    ctx.drawImage(
      this.betrIcon,
      beGroupStartX,
      betrBarCenterY - betrIconSize / 2,
      betrIconSize,
      betrIconSize,
    );
    ctx.font = `12px ${FONT_FAMILY}`;
    ctx.fillStyle = COLOR_AXIS_LABEL_GRAY;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(breakEvenLabel, beGroupStartX + betrIconSize + betrIconGap, betrBarCenterY);
    ctx.font = `bold 12px ${FONT_FAMILY}`;
    ctx.fillText(
      breakEvenValue,
      beGroupStartX + betrIconSize + betrIconGap + beLabelWidth,
      betrBarCenterY,
    );
    ctx.restore();

    // Future tax bar (rounded bottom corners only)
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(boxX - boxW / 2, futureBarY, boxW, TOOLTIP_FUTURE_TAX_H, [0, 0, 10, 10]);
    ctx.fillStyle = COLOR_FUTURE_TAX_BAR_BG;
    ctx.fill();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const futureTaxLabel = this.chartContent.futureTaxRate;
    const futureTaxValue = Math.round((this.futureTaxRate + Number.EPSILON) * 10000) / 100 + '%';
    ctx.font = `10px ${FONT_FAMILY}`;
    const labelWidth = ctx.measureText(futureTaxLabel + ' ').width;
    ctx.font = `bold 10px ${FONT_FAMILY}`;
    const valueWidth = ctx.measureText(futureTaxValue).width;
    const startX = boxX - (labelWidth + valueWidth) / 2;
    ctx.font = `10px ${FONT_FAMILY}`;
    ctx.fillStyle = '#fff';
    ctx.fillText(futureTaxLabel, startX + labelWidth / 2, futureBarY + TOOLTIP_FUTURE_TAX_H / 2);
    ctx.font = `bold 10px ${FONT_FAMILY}`;
    ctx.fillText(
      futureTaxValue,
      startX + labelWidth + valueWidth / 2,
      futureBarY + TOOLTIP_FUTURE_TAX_H / 2,
    );
    ctx.restore();

    // Border — drawn last so it sits on top of all inner fills
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(boxX - boxW / 2, boxY, boxW, boxH, 10);
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = '#222';
    ctx.stroke();
    ctx.restore();
  }

  private buildMobileTooltip(
    year: number,
    years: number[],
    withConversion: number[],
    withoutConversion: number[],
  ) {
    const idx = years.indexOf(year);
    const wc = idx !== -1 ? withConversion[idx] : withConversion[0];
    const woc = idx !== -1 ? withoutConversion[idx] : withoutConversion[0];
    const valuesEqual = this.truncate(wc) === this.truncate(woc);
    const desirable = wc > woc;
    const diff = wc - woc;
    const savedByText = desirable
      ? this.chartContent.savedByConversion
      : this.chartContent.lostByConversion;
    const betrAtYear = this.breakEvenData?.find((d) => d.year === year)?.value ?? 0;
    this.mobileTooltip = {
      headerText: this.formatCurrency(diff) + ' ' + savedByText,
      headerColor: valuesEqual
        ? COLOR_EQUAL_HEADER_BG
        : desirable
          ? COLOR_DESIRABLE_HEADER_BG
          : COLOR_UNDESIRABLE_HEADER_BG,
      headerTextColor: valuesEqual ? '#232323' : '#fff',
      withConvValue: this.formatCurrency(wc),
      withoutConvValue: this.formatCurrency(woc),
      withConvLabel: this.chartContent.withConversion,
      withoutConvLabel: this.chartContent.withoutConversion,
      valuesEqual,
      desirable,
      withConvOnTop: this.truncate(wc) >= this.truncate(woc),
      betrLabel: this.chartContent.breakEvenTaxRate,
      betrValue: Math.round((betrAtYear + Number.EPSILON) * 100) + '%',
      futureTaxLabel: this.chartContent.futureTaxRate,
      futureTaxValue: Math.round((this.futureTaxRate + Number.EPSILON) * 10000) / 100 + '%',
    };
    this.cdr.detectChanges();
  }

  private setupMouseListeners(canvas: HTMLCanvasElement, chart: Chart) {
    // eslint-disable-next-line max-statements
    this.mouseMoveListener = (e: MouseEvent) => {
      if (chart !== this.chartInstance) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const xScale = chart.scales.x;
      if (!xScale) return;
      const year = xScale.getValueForPixel(x);
      if (year === undefined || year === null) {
        return;
      }
      if (this.assetGrowthData) {
        const years = this.assetGrowthData.conversionLineData.map((d) => d.year);
        const closestYear = this.findClosestYear(years, year);
        this.hoveredYear = closestYear;
        if (this.isMobile) {
          this.buildMobileTooltip(
            closestYear,
            years,
            this.assetGrowthData.conversionLineData.map((d) => d.value),
            this.assetGrowthData.nonConversionLineData.map((d) => d.value),
          );
        }
        chart.draw();
      }
    };
    this.mouseLeaveListener = () => {
      if (chart !== this.chartInstance) return;
      this.hoveredYear = null;
      if (this.isMobile && this.assetGrowthData && this.defaultTooltipYear !== null) {
        const years = this.assetGrowthData.conversionLineData.map((d) => d.year);
        this.buildMobileTooltip(
          this.defaultTooltipYear,
          years,
          this.assetGrowthData.conversionLineData.map((d) => d.value),
          this.assetGrowthData.nonConversionLineData.map((d) => d.value),
        );
      }
      chart.draw();
    };

    canvas.addEventListener('mousemove', this.mouseMoveListener);
    canvas.addEventListener('mouseleave', this.mouseLeaveListener);
  }

  private removeMouseListeners(canvas: HTMLCanvasElement) {
    if (this.mouseMoveListener) {
      canvas.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = null;
    }
    if (this.mouseLeaveListener) {
      canvas.removeEventListener('mouseleave', this.mouseLeaveListener);
      this.mouseLeaveListener = null;
    }
  }

  private preloadImage(src: string): HTMLImageElement {
    const img = new Image();
    img.onload = () => this.chartInstance?.draw();
    img.src = src;
    return img;
  }

  private ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (maxWidth <= 0) return '';
    if (ctx.measureText(text).width <= maxWidth) return text;
    const ellipsis = '…';
    const ellipsisWidth = ctx.measureText(ellipsis).width;
    let lo = 0,
      hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (ctx.measureText(text.slice(0, mid)).width + ellipsisWidth <= maxWidth) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return lo === 0 ? '' : text.slice(0, lo) + ellipsis;
  }

  private truncate(n: number): number {
    return Math.trunc(n * 100) / 100;
  }

  private formatCurrency(val: number): string {
    return '$' + Math.round(Math.abs(val)).toLocaleString('en-US');
  }

  private findClosestYear(years: number[], target: number): number {
    if (!years || years.length === 0) return target;

    let closest = years[0];
    let minDist = Infinity;
    for (let i = 0; i < years.length; i++) {
      const dist = Math.abs(years[i] - target);
      if (dist < minDist) {
        minDist = dist;
        closest = years[i];
      }
    }
    return closest;
  }

  ngOnDestroy(): void {
    if (this.graphDataSub) this.graphDataSub.unsubscribe();
    if (this.futureTaxRateSub) this.futureTaxRateSub.unsubscribe();
    if (this.yearsUntilDistSub) this.yearsUntilDistSub.unsubscribe();
    if (this.howWillYouPaySub) this.howWillYouPaySub.unsubscribe();
    if (this.inputsValidSub) this.inputsValidSub.unsubscribe();
    if (this.inputsCompleteSub) this.inputsCompleteSub.unsubscribe();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    const canvas = this.chartCanvas?.nativeElement;
    if (canvas) {
      this.removeMouseListeners(canvas);
    }
    this.destroy();
  }

  getChartImageDataUrl(): string | null {
    const canvas = this.chartCanvas?.nativeElement;
    if (!canvas || !this.chartInstance) return null;
    return canvas.toDataURL('image/png');
  }

  destroy(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }
}
